/**
 * Display helpers for the intake review card.
 */

/**
 * Tone for the rating chip.
 *
 * DELIBERATELY NOT A VERDICT. The rating band predicts the outcome
 * sharply — 40-90% signs up 96-99%, no rating signs up 34% — so colouring
 * the bands by that rate would show the reviewer the model's opinion in
 * the guise of styling. That is the same contamination the hidden shadow
 * prediction exists to avoid: once the card nudges, neither the decision
 * nor the measurement of the model against it means anything.
 *
 * So every band that states a real rating reads neutral. Amber is
 * reserved for a rating that is missing or unreadable, which is a
 * statement about the evidence rather than about the case.
 */
export const ratingTone = (band) => {
  if (!band) return "neutral";
  if (band === "not recorded" || band === "unclear") return "review";
  return "neutral";
};

export const ratingChipText = (rating) => {
  if (!rating) return "Rating not recorded";
  const { band, display } = rating;
  if (band === "not recorded") return "Rating not recorded";
  if (band === "unclear") return display ? `Rating unclear — "${display}"` : "Rating unclear";
  if (band === "no rating / no claim") return "No rating / no claim";
  return band;
};

/** "3 days" / "today" / "1 day" — an age a person reads without parsing. */
export const ageLabel = (days) => {
  if (typeof days !== "number" || Number.isNaN(days)) return "";
  if (days <= 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
};

/**
 * Seconds the card was held, from the moment it opened.
 *
 * Clamped to the hour the backend also enforces, so a card left open over
 * lunch reports nothing rather than a fictional decision time.
 */
export const secondsOnCard = (openedAt) => {
  if (!openedAt) return undefined;
  const elapsed = Math.round((Date.now() - openedAt) / 1000);
  if (elapsed < 0 || elapsed > 3600) return undefined;
  return elapsed;
};

/** The lead facts keyed for lookup, tolerating a malformed snapshot. */
export const leadByKey = (snapshot) => {
  const entries = snapshot?.lead;
  if (!Array.isArray(entries)) return {};
  return entries.reduce((acc, entry) => {
    if (entry && entry.key) acc[entry.key] = entry;
    return acc;
  }, {});
};
